import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowDownUp, FileUp, LayoutGrid, List, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/Button';
import { Menu } from '@/components/ui/Menu';
import { t, type TKey, tr } from '@/i18n';
import { api, errorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useBoards, useProjects, type BoardsView } from '@/lib/queries';
import type { Board } from '@/lib/types';
import { BoardCard, BoardCardSkeleton, BoardRow } from './BoardCard';
import { useDashboard } from './DashboardLayout';
import { QuickStart } from './QuickStart';

const TITLES: Record<BoardsView, TKey> = {
  all: 'dashboard.allBoards',
  recent: 'dashboard.recent',
  favorites: 'dashboard.favorites',
  shared: 'dashboard.shared',
  trash: 'dashboard.trash',
  templates: 'dashboard.templates',
};

function useViewMode(): ['grid' | 'list', (v: 'grid' | 'list') => void] {
  const [mode, setMode] = useState<'grid' | 'list'>(() => {
    try {
      return localStorage.getItem('lumo-dash-view') === 'list' ? 'list' : 'grid';
    } catch {
      return 'grid';
    }
  });
  return [
    mode,
    (v) => {
      setMode(v);
      try {
        localStorage.setItem('lumo-dash-view', v);
      } catch {
        /* ignore */
      }
    },
  ];
}

export function useCreateBoard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return async (opts: { workspaceId: string; projectId?: string | null; templateId?: string; name?: string }) => {
    try {
      const { board } = await api<{ board: Board }>('/api/boards', {
        body: { name: opts.name ?? t('board.untitled'), workspaceId: opts.workspaceId, projectId: opts.projectId ?? null, templateId: opts.templateId },
      });
      void qc.invalidateQueries({ queryKey: ['boards'] });
      navigate(`/board/${board.id}`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
}

/** Новая доска из файла экспорта (.lumo.json). */
function ImportBoardButton({ workspaceId, projectId }: { workspaceId: string; projectId: string | null }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (file: File) => {
    setBusy(true);
    try {
      if (file.size > 20 * 1024 * 1024) throw new Error(t('dashboard.importTooLarge'));
      let data: { board?: { name?: string } };
      try {
        data = JSON.parse(await file.text()) as typeof data;
      } catch {
        throw new Error(t('dashboard.importNotJson'));
      }
      const name = data.board?.name || file.name.replace(/\.lumo\.json$|\.json$/i, '');
      const { board } = await api<{ board: Board }>('/api/boards/import', { body: { workspaceId, projectId, name, data } });
      void qc.invalidateQueries({ queryKey: ['boards'] });
      navigate(`/board/${board.id}`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  return (
    <>
      <input ref={input} type="file" accept=".json,application/json" className="hidden" onChange={(e) => e.target.files?.[0] && void onFile(e.target.files[0])} />
      <Button variant="secondary" icon={<FileUp />} loading={busy} onClick={() => input.current?.click()}>
        {t('dashboard.import')}
      </Button>
    </>
  );
}

export function BoardsPage({ view }: { view: BoardsView }) {
  const { workspace } = useDashboard();
  const { projectId } = useParams();
  const projects = useProjects(workspace.id);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<'updated' | 'name' | 'created'>('updated');
  const [mode, setMode] = useViewMode();
  const create = useCreateBoard();
  const boards = useBoards({ view, workspaceId: workspace.id, projectId, q: q.trim() || undefined, sort });
  const project = projectId ? projects.data?.find((p) => p.id === projectId) : null;
  const title = project ? project.name : t(TITLES[view]);
  const canCreate = workspace.role !== 'guest' && view !== 'trash' && view !== 'shared';
  const trash = view === 'trash';

  const empty = useMemo(() => {
    if (q.trim()) return <EmptyState variant="search" title={t('dashboard.emptySearch')} text={t('dashboard.emptySearchText')} />;
    switch (view) {
      case 'favorites':
        return <EmptyState variant="favorites" title={t('dashboard.emptyFavorites')} text={t('dashboard.emptyFavoritesText')} />;
      case 'shared':
        return <EmptyState variant="shared" title={t('dashboard.emptyShared')} text={t('dashboard.emptySharedText')} />;
      case 'trash':
        return <EmptyState variant="trash" title={t('dashboard.emptyTrash')} text={t('dashboard.emptyTrashText')} />;
      case 'recent':
        return <EmptyState title={t('dashboard.emptyRecent')} />;
      default:
        return (
          <EmptyState
            title={t('dashboard.emptyAll')}
            text={t('dashboard.emptyAllText')}
            action={
              canCreate && (
                <Button variant="primary" icon={<Plus />} onClick={() => create({ workspaceId: workspace.id, projectId })}>
                  {t('dashboard.newBoard')}
                </Button>
              )
            }
          />
        );
    }
  }, [q, view, canCreate, create, workspace.id, projectId]);

  const list = boards.data ?? [];
  return (
    <div className="mx-auto w-full max-w-7xl px-5 py-7 sm:px-8">
      <header className="flex flex-wrap items-center gap-3">
        <div className="mr-auto min-w-0">
          <h1 className="truncate text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-0.5 text-sm text-fg-subtle">
            {trash ? t('dashboard.trashHint') : boards.data ? t('dashboard.boardsCount', { count: list.length }) : ' '}
          </p>
        </div>
        <label className="relative flex h-9 w-full items-center sm:w-56">
          <Search className="pointer-events-none absolute left-3 size-4 text-fg-subtle" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('dashboard.searchBoards')}
            aria-label={t('dashboard.searchBoards')}
            className="h-full w-full rounded-control bg-surface pr-3 pl-9 text-sm shadow-[inset_0_0_0_1px_var(--lumo-border-strong)] outline-none placeholder:text-fg-subtle focus:shadow-[inset_0_0_0_1.5px_var(--color-accent),0_0_0_4px_var(--lumo-ring)]"
          />
        </label>
        {view !== 'recent' && view !== 'favorites' && (
          <Menu
            side="bottom"
            align="end"
            items={(['updated', 'name', 'created'] as const).map((s) => ({
              value: s,
              label: t(s === 'updated' ? 'dashboard.sortUpdated' : s === 'name' ? 'dashboard.sortName' : 'dashboard.sortCreated'),
              shortcut: sort === s ? '✓' : undefined,
              onSelect: () => setSort(s),
            }))}
            trigger={
              <button type="button" aria-label={tr("Сортировка")} title={tr("Сортировка")} className="inline-flex size-9 items-center justify-center rounded-control text-fg-muted hover:bg-hover hover:text-fg">
                <ArrowDownUp className="size-4" />
              </button>
            }
          />
        )}
        <div role="radiogroup" aria-label={tr("Вид")} className="inline-flex rounded-control bg-surface-2 p-0.5">
          {(['grid', 'list'] as const).map((m) => {
            const Icon = m === 'grid' ? LayoutGrid : List;
            return (
              <button
                key={m}
                role="radio"
                aria-checked={mode === m}
                aria-label={t(m === 'grid' ? 'dashboard.viewGrid' : 'dashboard.viewList')}
                onClick={() => setMode(m)}
                className={cn('inline-flex h-8 w-9 items-center justify-center rounded-[8px]', mode === m ? 'bg-surface text-fg shadow-card' : 'text-fg-subtle hover:text-fg')}
              >
                <Icon className="size-4" />
              </button>
            );
          })}
        </div>
        {canCreate && <ImportBoardButton workspaceId={workspace.id} projectId={projectId ?? null} />}
        {canCreate && (
          <Button variant="primary" icon={<Plus />} onClick={() => create({ workspaceId: workspace.id, projectId })}>
            {t('dashboard.newBoard')}
          </Button>
        )}
      </header>

      {view === 'all' && !projectId && canCreate && !q && <QuickStart workspaceId={workspace.id} />}

      <section className="mt-7" aria-busy={boards.isLoading}>
        {boards.isLoading ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-5">
            {Array.from({ length: 8 }, (_, i) => (
              <BoardCardSkeleton key={i} />
            ))}
          </div>
        ) : !list.length ? (
          empty
        ) : mode === 'grid' ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-5">
            {list.map((b, i) => (
              <BoardCard key={b.id} board={b} projects={projects.data ?? []} trash={trash} index={i} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col">
            <div className="hidden grid-cols-[minmax(0,1fr)_160px_140px_auto] gap-3 px-2 pb-2 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase sm:grid">
              <span>{t('dashboard.name')}</span>
              <span>{t('dashboard.owner')}</span>
              <span>{trash ? tr("Удалено") : tr("Изменено")}</span>
              <span className="w-8" />
            </div>
            {list.map((b) => (
              <BoardRow key={b.id} board={b} projects={projects.data ?? []} trash={trash} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
